// CanvasConnection — single shared edge renderer for the canvas
// panzoom surfaces (/studios, /team-and-agents, future canvas pages).
//
// Each connection mounts as a `<g>` with two paths:
//   1. HitArea — a fat transparent stroke that catches clicks /
//      hover; `pointer-events: stroke` so only the line catches
//      events, leaving the rest of the SVG transparent for panzoom.
//   2. Visible — the thin stroke the user sees.
//
// Bezier math defers to `el.call('bezierBetweenNodes', …)` so both
// pages agree on the curve shape — no per-page math drift.
//
// Wire shape (child state when used with childrenAs:'state'):
//   {
//     from: nodeId, to: nodeId,
//     // optional: per-edge styling overrides (selected/hover state
//     // is read from root via the state keys the page configures
//     // through scope below)
//     shared?: [teamNames],     // co-membership tooltip
//     kind?: 'integration',     // visual variant
//     // Plus: the page's scope-injected helpers:
//     getPath:    (root, from, to, el) => 'M…' SVG path
//     selectedKey?: (root) => 'from→to' key of the selected edge
//     hoverKey?:    (root) => 'from→to' key of the hovered edge
//     onSelect?:    (e, el, s) => …   click handler
//   }
//
// Pages pass per-instance config via the `scope: { … }` prop on the
// parent Connections container (smbls v3.14 FA204 — one-shot consts
// live in scope, not in mutable state). Children inherit through
// state, so each connection's reactive factories see them as `s.X`.

export const CanvasConnection = {
  tag: 'g',
  attr: {
    'data-connection-from': (el, s) => s.from,
    'data-connection-to': (el, s) => s.to,
    'data-shared-count': (el, s) => Array.isArray(s.shared) ? String(s.shared.length) : '0'
  },
  title: (el, s) => Array.isArray(s.shared) && s.shared.length > 0
    ? `Shared teams: ${s.shared.join(', ')}`
    : '',
  style: { cursor: 'pointer', pointerEvents: 'visiblePainted' },

  HitArea: {
    tag: 'path',
    attr: {
      d: (el, s) => (typeof s.getPath === 'function' ? s.getPath(s.root, s.from, s.to, el) : ''),
      stroke: 'transparent',
      'stroke-width': '14',
      fill: 'none',
      'pointer-events': 'stroke',
      'vector-effect': 'non-scaling-stroke'
    },
    onClick: (e, el, s) => {
      if (typeof s.onSelect === 'function') s.onSelect(e, el, s)
    },
    onMouseenter: (e, el, s) => {
      if (typeof s.onHover === 'function') s.onHover(e, el, s, true)
    },
    onMouseleave: (e, el, s) => {
      if (typeof s.onHover === 'function') s.onHover(e, el, s, false)
    }
  },

  Visible: {
    tag: 'path',
    attr: {
      d: (el, s) => (typeof s.getPath === 'function' ? s.getPath(s.root, s.from, s.to, el) : ''),
      stroke: (el, s) => {
        if (typeof s.getStroke === 'function') return s.getStroke(s.root, s)
        return 'rgba(255,255,255,0.5)'
      },
      'stroke-width': (el, s) => {
        if (typeof s.getStrokeWidth === 'function') return String(s.getStrokeWidth(s.root, s))
        return '1.5'
      },
      'stroke-dasharray': (el, s) => {
        if (typeof s.getDash === 'function') return s.getDash(s.root, s) || '0'
        return '0'
      },
      fill: 'none',
      'pointer-events': 'none',
      'vector-effect': 'non-scaling-stroke',
      'stroke-linecap': 'round'
    }
  }
}
